import { getRandomPartialColor } from '$lib/helper/color';
import { generateUUID } from '$lib/helper/uuid';
import type { BookableGiftcard, Giftcard, GiftcardStats } from '$lib/model/giftcard';
import { infinityListReloadStore, type ListStore } from '$lib/stores/infinity-list';
import type { Axios } from 'axios';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { apiStore } from './api';
import { customerStore } from './customer';
import { CurrencyCode, formatCurrency, t } from './i18n';
import { toast } from './toast';

/**
 * Giftcard Store
 */
export const giftcardStore = writable<ListStore<Giftcard> & { selected: Giftcard | null }>({
	data: [],
	isFresh: true,
	apiSearchUrl: '/giftcard/search',
	selected: null
});

export const bookableGiftcardStore = writable<BookableGiftcard[]>([]);
export const bookableGiftcardSelectedStore = writable<BookableGiftcard | undefined>(undefined);
export const defaultGiftcard: BookableGiftcard = {
	id: '',
	realPrice: 50,
	cardColor: '#ffffff',
	onlinePrice: 50,
	label: '',
	description: '',
	publishStart: null,
	publishEnd: null
};

/**
 * Giftcard Stats Store
 */
export const giftcardStatsStore = writable<GiftcardStats>({
	sold: 0,
	income: 0,
	remaining: 0
});

export function giftcardSearch(code: string): Promise<Giftcard | undefined> {
	return (get(apiStore) as Axios)
		.post('/giftcard/search', { code })
		.then((res: { data: Giftcard[] }) => {
			if (res.data.length === 0) {
				return undefined;
			}
			const customer = res.data[0].customer;
			if (customer) customerStore.set([...get(customerStore), customer]);
			return apiToAppGiftcardTransformer(res.data[0]);
		});
}

export function apiToAppGiftcardTransformer(giftcard: Giftcard): Giftcard {
	return {
		...giftcard,
		expiresAt: giftcard.expiresAt
			? DateTime.fromISO(giftcard.expiresAt as unknown as string)
			: null
	};
}

export function giftcardConsume(giftcard: Giftcard, amount: number): Promise<void> {
	return (get(apiStore) as Axios)
		.post('/giftcard/' + giftcard.id + '/consume', { amount: amount })
		.then(() => {
			get(infinityListReloadStore)['giftcard-list'] = true;
			get(toast)({
				text: get(t)('giftcard.consume.consumed', {
					amount: formatCurrency(
						(giftcard.remainingAmount ?? amount) - amount,
						CurrencyCode.EUR
					)
				}),
				bgColor: 'success'
			});
		});
}

export function loadBookableGiftcards(): Promise<void> {
	return (get(apiStore) as Axios).get<BookableGiftcard[]>('/giftcard/bookable').then((res) => {
		loadBookableGiftcardsFromData(res.data);
	});
}

export function loadBookableGiftcardsFromData(data: BookableGiftcard[]): void {
	bookableGiftcardStore.set(
		data.map((giftcard) => ({
			...giftcard,
			publishStart: giftcard.publishStart
				? DateTime.fromISO(giftcard.publishStart as unknown as string)
				: null,
			publishEnd: giftcard.publishEnd
				? DateTime.fromISO(giftcard.publishEnd as unknown as string)
				: null
		}))
	);
}

export function loadGiftcardStatsFromData(data: GiftcardStats): void {
	giftcardStatsStore.set(data);
}

export function generateNewGiftcard(): BookableGiftcard {
	return {
		id: generateUUID(),
		realPrice: 50,
		onlinePrice: 50,
		cardColor: getRandomPartialColor(),
		label: '',
		description: '',
		publishStart: null,
		publishEnd: null
	};
}
